import moment from 'moment'
import { useState } from 'react'

const TIME_RANGES = [
  { label: 'Morning (9 AM – 12 PM)', value: '09:00–12:00' },
  { label: 'Afternoon (12 PM – 3 PM)', value: '12:00–15:00' },
  { label: 'Late Afternoon (3 PM – 5:30 PM)', value: '15:00–17:30' },
]

function isValidNanp(raw: string): boolean {
  const digits = raw.replace(/\D/g, '')
  const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (normalized.length !== 10) return false
  if (['0', '1'].includes(normalized[0])) return false
  if (['0', '1'].includes(normalized[3])) return false
  return true
}

function isWeekendDate(value: string): boolean {
  if (!value) return false
  const day = moment(value).day()
  return day === 0 || day === 6
}

interface CallBackFormProps {
  suggestedSubject?: string
  respond: (result: string) => void | Promise<void>
}

export function CallBackForm({ suggestedSubject, respond }: CallBackFormProps) {
  const [phone, setPhone] = useState('')
  const [subject, setSubject] = useState(suggestedSubject ?? '')
  const [note, setNote] = useState('')
  const [date, setDate] = useState('')
  const [timeRange, setTimeRange] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const phoneValid = isValidNanp(phone)
  const canSubmit = phoneValid && subject.trim() && date && !isWeekendDate(date) && timeRange && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/request-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: `+1 ${phone.trim()}`,
          subject: subject.trim(),
          note: note.trim(),
          date: moment(date).format('MMMM D, YYYY'),
          timeRange: TIME_RANGES.find((t) => t.value === timeRange)?.label ?? timeRange,
        }),
      })
      if (!res.ok) throw new Error('failed')
      setDone(true)
      respond(JSON.stringify({ phone: phone.trim(), date, timeRange }))
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className='my-3 flex items-center gap-3 overflow-hidden rounded-49xl bg-white/80 px-4 py-3.5 shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34C759]'>
          <svg className='h-3.5 w-3.5 text-white' fill='currentColor' viewBox='0 0 20 20'>
            <path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
          </svg>
        </div>
        <p className='text-[15px] text-black'>
          Your callback request has been submitted. We&apos;ll be in touch soon.
        </p>
      </div>
    )
  }

  return (
    <div className='my-3 overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
      {/* Header */}
      <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#34C759] to-[#248A3D] shadow-sm'>
          <span className='text-xl leading-none'>📞</span>
        </div>
        <div>
          <p className='text-[17px] font-semibold leading-snug text-black'>Request a callback</p>
          <p className='text-[13px] leading-snug text-[#6C6C70]'>
            Fill in your details and we&apos;ll call you back.
          </p>
        </div>
      </div>

      {/* Phone + Subject + Note */}
      <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Phone number <span className='normal-case tracking-normal'>(US / Canada)</span>
          </p>
          <div className='flex items-center gap-2'>
            <span className='shrink-0 text-[17px] text-[#8E8E93]'>+1</span>
            <input
              type='tel'
              value={phone}
              disabled={submitting}
              onChange={(e) => setPhone(e.target.value)}
              placeholder='(555) 000-0000'
              className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
            />
          </div>
          {phone && !phoneValid && (
            <p className='mt-1 text-[12px] text-[#FF3B30]'>Please enter a valid US or Canadian number.</p>
          )}
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>Subject</p>
          <input
            value={subject}
            disabled={submitting}
            onChange={(e) => setSubject(e.target.value)}
            placeholder='What would you like to discuss?'
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Note <span className='normal-case tracking-normal text-[#B0B0B5]'>(optional)</span>
          </p>
          <textarea
            rows={2}
            value={note}
            disabled={submitting}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything you'd like us to know…"
            className='w-full resize-none bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
      </div>

      {/* Date + Time */}
      <div className='mx-4 mt-3 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Date <span className='normal-case tracking-normal text-[#B0B0B5]'>(Mon – Fri, Eastern)</span>
          </p>
          <input
            type='date'
            min={moment().add(1, 'day').format('YYYY-MM-DD')}
            value={date}
            disabled={submitting}
            onChange={(e) => setDate(e.target.value)}
            className='w-full bg-transparent text-[17px] text-black outline-none disabled:opacity-40'
          />
          {date && isWeekendDate(date) && (
            <p className='mt-1 text-[12px] text-[#FF3B30]'>Please select a weekday (Mon – Fri).</p>
          )}
        </div>
      </div>

      {/* Time range pills */}
      <div className='mx-4 mt-3 flex flex-col gap-2'>
        {TIME_RANGES.map((t) => (
          <button
            key={t.value}
            type='button'
            onClick={() => setTimeRange(t.value)}
            disabled={submitting}
            className={`rounded-[14px] px-4 py-3 text-left text-[15px] font-medium transition-all active:scale-[0.98] ${
              timeRange === t.value
                ? 'bg-[rgba(0,0,0,0.10)] text-black shadow-sm'
                : 'bg-[rgba(120,120,128,0.08)] text-black hover:bg-[rgba(120,120,128,0.14)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className='mx-5 mt-2 text-[13px] text-[#FF3B30]'>{error}</p>}

      {/* Button */}
      <div className='px-4 pb-5 pt-4'>
        <button
          type='button'
          disabled={!canSubmit}
          onClick={handleSubmit}
          className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
        >
          {submitting ? 'Confirming…' : 'Confirm Callback'}
        </button>
      </div>
    </div>
  )
}
